import React from "react";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import DrawIcon from "@mui/icons-material/Draw";
import { formatDateLocal } from "../../../../utils/hooks/dataHandler";
import TitleLabel from "../utils/FormTitle";
import { TextFieldForName } from "../../../utils/TextField/TextField";
import CustomDatePicker from "../../../utils/DatePicker/CustomDatePicker";
import FormCSDraftReviewPdf from "./FormCSDraftReviewPdf";
import {
  FORM_CS_DECLARATION_ITEMS,
  FORM_CS_FINAL_CONFIRMATION_LABEL,
  FORM_CS_TAX_AGENT_DECLARATION_LABEL,
} from "./formCSDeclarationContent";
import { isValidFormCSUen } from "./formCSDefaults";

export {
  FORM_CS_DECLARATION_ITEMS,
  FORM_CS_FINAL_CONFIRMATION_LABEL,
  FORM_CS_TAX_AGENT_DECLARATION_LABEL,
};

export const FormCSSubmitterCapacitySection = ({
  declarationAck,
  onAckChange,
  taxpayerUen,
  disabled,
}) => {
  const isTaxAgent = declarationAck.submitterCapacity === "TAX_AGENT";
  const taxAgentUen = declarationAck.taxAgentUen || "";

  const setAck = (field, value) => {
    onAckChange({ ...declarationAck, [field]: value });
  };

  return (
    <Paper variant='outlined' sx={{ p: 2.5 }}>
      <Typography variant='subtitle1' fontWeight={700}>
        Submission Authority
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
        Declare whether this return is submitted by the taxpayer or by an
        authorised tax agent. These details will be recorded with the
        submission.
      </Typography>

      <RadioGroup
        value={declarationAck.submitterCapacity || ""}
        onChange={(event) => {
          const submitterCapacity = event.target.value;
          onAckChange({
            ...declarationAck,
            submitterCapacity,
            taxAgentUen:
              submitterCapacity === "TAX_AGENT"
                ? declarationAck.taxAgentUen || ""
                : "",
            isTaxAgentAuthorised:
              submitterCapacity === "TAX_AGENT"
                ? declarationAck.isTaxAgentAuthorised
                : false,
          });
        }}
        sx={{ mt: 1.5 }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
          {[
            {
              value: "TAXPAYER",
              title: "Taxpayer / Company Representative",
              description:
                "Submitted directly by the taxpayer or its authorised company representative.",
            },
            {
              value: "TAX_AGENT",
              title: "Authorised Tax Agent",
              description: "Submitted by a tax agent acting for the taxpayer.",
            },
          ].map((option) => (
            <Paper
              key={option.value}
              variant='outlined'
              sx={{
                flex: 1,
                p: 2,
                borderColor:
                  declarationAck.submitterCapacity === option.value
                    ? "primary.main"
                    : "divider",
              }}
            >
              <FormControlLabel
                value={option.value}
                control={<Radio disabled={disabled} />}
                label={
                  <Box sx={{ ml: 0.5 }}>
                    <Typography variant='body2' fontWeight={700}>
                      {option.title}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {option.description}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
              />
            </Paper>
          ))}
        </Stack>
      </RadioGroup>

      {declarationAck.submitterCapacity === "TAXPAYER" && (
        <Box sx={{ mt: 2 }}>
          <TitleLabel title='Taxpayer UEN' />
          <TextField
            fullWidth
            size='small'
            value={taxpayerUen || ""}
            helperText='This is the company UEN used in the Form C-S filing.'
            InputProps={{ readOnly: true }}
            disabled={disabled}
          />
        </Box>
      )}

      {isTaxAgent && (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <Box>
            <TitleLabel title='Tax Agent UEN' required />
            <TextField
              fullWidth
              size='small'
              value={taxAgentUen}
              onChange={(event) =>
                setAck(
                  "taxAgentUen",
                  event.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "")
                )
              }
              inputProps={{ maxLength: 10 }}
              placeholder='UEN of the tax agent firm'
              error={Boolean(taxAgentUen) && !isValidFormCSUen(taxAgentUen)}
              helperText='Enter the 9 or 10 character UEN of the tax agent firm.'
              disabled={disabled}
            />
          </Box>
          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(declarationAck.isTaxAgentAuthorised)}
                  onChange={(event) =>
                    setAck("isTaxAgentAuthorised", event.target.checked)
                  }
                  disabled={disabled}
                />
              }
              label={
                <Typography variant='body2' fontWeight={600}>
                  {FORM_CS_TAX_AGENT_DECLARATION_LABEL}
                </Typography>
              }
              sx={{ alignItems: "flex-start", m: 0 }}
            />
          </Box>
        </Stack>
      )}
    </Paper>
  );
};

const FormCSDeclarationSection = ({
  company,
  declaration,
  onChange,
  declarationAck,
  onAckChange,
  filingInfo,
  dataFormCS,
  selectedTc,
  referenceSummary,
  disabled,
}) => {
  const setDecl = (key, checked) => {
    onChange({ ...declaration, [key]: checked });
  };

  const setAck = (field, value) => {
    onAckChange({ ...declarationAck, [field]: value });
  };

  const setFinalConfirmation = (checked) => {
    onAckChange({
      ...declarationAck,
      isAuthorisedAccurate: checked,
      hasConfirmedAllDeclarations: checked,
    });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, mt: -2 }}
        >
          <GavelIcon color='primary' />
          <Typography variant='h6' fontWeight={600}>
            Declaration
          </Typography>
        </Box>
        <Typography variant='body2' color='text.secondary'>
          Review and confirm all declarations required by IRAS, then sign to
          certify the accuracy and completeness of the return prior to
          submission.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <FormCSSubmitterCapacitySection
          declarationAck={declarationAck}
          onAckChange={onAckChange}
          taxpayerUen={filingInfo?.uen || company?.uen}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={12}>
        <Paper variant='outlined' sx={{ p: 2 }}>
          <Typography variant='subtitle2' fontWeight={700} sx={{ mb: 1.5 }}>
            IRAS declarations
          </Typography>
          <Grid container spacing={0.5}>
            {FORM_CS_DECLARATION_ITEMS.map((item) => (
              <Grid item xs={12} key={item.key}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(declaration[item.key])}
                      onChange={(e) => setDecl(item.key, e.target.checked)}
                      disabled={disabled}
                    />
                  }
                  label={
                    <Typography variant='body2'>
                      {item.label}
                      {item.optional ? (
                        <Typography
                          component='span'
                          variant='body2'
                          color='text.secondary'
                        >
                          {" "}
                          (optional)
                        </Typography>
                      ) : null}
                    </Typography>
                  }
                  sx={{ mb: 1 }}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper variant='outlined' sx={{ p: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <DrawIcon color='primary' fontSize='small' />
            <Typography variant='subtitle1' fontWeight={700}>
              Signature
            </Typography>
          </Box>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Enter your name to confirm that you have reviewed and agreed to all
            declarations above.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TitleLabel title='Declaration name' required />
              <TextFieldForName
                fullWidth
                value={declarationAck.declarantName}
                onChange={(val) => setAck("declarantName", val)}
                disabled={disabled}
                placeholder='Full name of signatory'
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TitleLabel title='Date of declaration' required />
              <CustomDatePicker
                placeholder='Date of declaration'
                selected={
                  declarationAck.declarationDate
                    ? new Date(declarationAck.declarationDate)
                    : null
                }
                onChange={(date) => {
                  setAck("declarationDate", date ? formatDateLocal(date) : "");
                }}
                fullWidth
                required
                disabled={disabled}
              />
            </Grid>
          </Grid>

          <FormCSDraftReviewPdf
            company={company}
            filingInfo={filingInfo}
            dataFormCS={dataFormCS}
            declaration={declaration}
            declarationAck={declarationAck}
            selectedTc={selectedTc}
            referenceSummary={referenceSummary}
            disabled={disabled}
          />

          <Divider sx={{ my: 2.5 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(
                  declarationAck.isAuthorisedAccurate &&
                  declarationAck.hasConfirmedAllDeclarations
                )}
                onChange={(e) => setFinalConfirmation(e.target.checked)}
                disabled={disabled}
              />
            }
            label={
              <Typography variant='body2' fontWeight={600}>
                {FORM_CS_FINAL_CONFIRMATION_LABEL}
              </Typography>
            }
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default FormCSDeclarationSection;
